import { timeFormat } from 'd3-time-format';
import { format, FormatSpecifier } from 'd3-format';
import { timeMap } from '@/utils/timeFormatters';
import intlFormatter from '@/utils/currency';

function specs(obj) {
  const { type, comma } = obj;
  let style = 's';

  const digits = '~';
  const symbol = undefined;

  if (type === 'percent') {
    style = '%';
  }

  const fs = new FormatSpecifier({
    symbol,
    type: `${digits}${style}`,
    comma,
  });

  return fs;
}

const axisFormat = (fs, obj) => {
  const decimalFs = new FormatSpecifier({ type: '~r', comma: obj.comma });
  const isDecimal = (label) => Number(label) < 1 && Number(label) > -1;
  const upperCaseSIFormatter = (label) =>
    isDecimal(label)
      ? format(decimalFs)(label).toUpperCase().replace(/G/, 'B')
      : format(fs)(label).toUpperCase().replace(/G/, 'B'); // SI formatter has Billions as G instead of B

  if (fs.type.indexOf('%') !== -1) {
    const { transpose } = obj;
    return (label, index, totalTicks) => {
      const firstTick = transpose ? 0 : totalTicks - 1;
      return `${index === firstTick ? `${label}%` : label}`.replace('-', '–');
    };
  }

  if (obj.type === 'currency') {
    const { ISO, transpose } = obj;
    return (label, index, totalTicks) => {
      const firstTick = transpose ? 0 : totalTicks - 1;
      return index === firstTick ? intlFormatter(label, ISO) : upperCaseSIFormatter(label);
    };
  }

  return (label) => upperCaseSIFormatter(label);
};

const { searchParams } = new URL(window.location.href);

const hours = 60 * 60 * 1000;
const CET = 1 * hours;
const GMT = 0;
const EST = -5 * hours;

const tzOffset = {
  'de-de': CET,
  'en-ca': EST,
  'en-gb': GMT,
  'en-us': EST,
  'es-es': CET,
  'fr-ca': EST,
  'fr-fr': CET,
  'it-it': CET,
  'nl-nl': CET,
};

const locale = searchParams.get('locale') || 'en-us';

const template = {
  date(epoch) {
    const offset = new Date().getTimezoneOffset() * 60 * 1000;
    const localeOffset = tzOffset[locale in tzOffset ? locale : 'en-us'];
    return new Date(epoch + offset + localeOffset);
  },
  format,
  axisFormat,
  timeFormat,
  multiYear: (d) => (timeFormat('%m')(d) === '01' ? timeFormat('%Y')(d) : timeFormat('%b')(d)),
  ...timeMap,
};

export default function revive(obj, options = {}) {
  const isArray = Array.isArray(obj);
  if (!obj) {
    return obj;
  }
  return (isArray ? obj : Object.keys(obj)).reduce(
    (parsed, name, ndx) => {
      const key = isArray ? ndx : name;
      let item = obj[key];

      if (item && item.eval && item.eval in template) {
        item = template[item.eval];
      } else if (typeof item === 'object') {
        item = revive(item, options);
      }

      if (options.simple ? key === 'date' : true) {
        if (typeof item === 'function' && obj[key].apply) {
          if (typeof obj[key].apply === 'object' && !Array.isArray(obj[key].apply)) {
            const def = obj[key].apply;
            const prop = obj[key].prop ?? key;

            parsed[key] = obj[key];
            parsed[prop] = item.call(null, specs(def, ndx), def);
          } else {
            const { prop, apply } = obj[key];

            if (prop) {
              parsed[key] = obj[key];
              parsed[prop] = item.call(null, apply);
            } else if (typeof apply !== 'function') {
              parsed[key] = item.apply(null, apply);
            }
          }
        } else {
          parsed[key] = item;
        }
      }

      return parsed;
    },
    isArray ? [] : {}
  );
}
